import {
  getLocalTimeZone,
  toCalendarDateTime,
  toZoned,
  type ZonedDateTime,
} from "@internationalized/date";
import React from "react";
import type { DatePickerProps, Key } from "react-aria-components";

import {
  selectableTimezones as defaultSelectableTimezones,
  zoneMapping as defaultZoneMapping,
} from "@/lib/utils/tz";
import { DatePicker } from "./date-picker";
import { TzSelect, type TzSelectProps } from "./tz-select";

type DatePickerWithZoneProps = DatePickerProps<ZonedDateTime> & {
  selectableTimezones?: string[];
  zoneMapping?: Record<string, string>;
};

type DatePickerWithZoneContextValue = {
  selectableTimezones?: string[];
  zoneMapping?: Record<string, string>;
  tz: string;
  setTz: (tz: string) => void;
  value?: ZonedDateTime | null;
  onChange?: (date: ZonedDateTime | null) => void;
};

const DatePickerWithZoneContext = React.createContext(
  {} as DatePickerWithZoneContextValue,
);

export function DatePickerWithZone({
  selectableTimezones = defaultSelectableTimezones,
  zoneMapping = defaultZoneMapping,
  value,
  onChange,
  ...props
}: DatePickerWithZoneProps) {
  return (
    <DatePickerWithZoneProvider
      value={value}
      onChange={onChange}
      selectableTimezones={selectableTimezones}
      zoneMapping={zoneMapping}
    >
      <DatePickerWithZoneInner {...props} />
    </DatePickerWithZoneProvider>
  );
}

function DatePickerWithZoneInner(props: DatePickerProps<ZonedDateTime>) {
  const { tz, value, onChange } = React.useContext(DatePickerWithZoneContext);

  const handleDateChange = (newDate: ZonedDateTime | null) => {
    if (!newDate) {
      onChange?.(null);
      return;
    }
    const calendarDate = toCalendarDateTime(newDate);
    const newZoned = toZoned(calendarDate, tz);
    onChange?.(newZoned);
  };

  return (
    <DatePicker
      {...props}
      hideTimeZone
      value={value}
      onChange={handleDateChange}
    />
  );
}

export function DatePickerTimeZone(props: TzSelectProps) {
  const { value, onChange, tz, setTz, selectableTimezones, zoneMapping } =
    React.useContext(DatePickerWithZoneContext);

  const handleTzChange = (key: Key | null) => {
    if (!key) return;
    setTz(key as string);
    if (value) {
      const calendarDate = toCalendarDateTime(value);
      const newZoned = toZoned(calendarDate, key as string);
      onChange?.(newZoned);
    }
  };

  React.useEffect(() => {
    if (value) {
      setTz(value.timeZone ?? getLocalTimeZone());
    }
  }, [value, setTz]);

  return (
    <TzSelect
      {...props}
      selectedKey={tz}
      onSelectionChange={(key) => handleTzChange(key as string)}
      selectableTimezones={selectableTimezones}
      zoneMapping={zoneMapping}
    />
  );
}

function DatePickerWithZoneProvider({
  children,
  value,
  onChange,
  selectableTimezones,
  zoneMapping,
}: Omit<DatePickerWithZoneContextValue, "tz" | "setTz"> & {
  children: React.ReactNode;
}) {
  const [tz, setTz] = React.useState(value?.timeZone ?? getLocalTimeZone());

  return (
    <DatePickerWithZoneContext.Provider
      value={{ tz, setTz, value, onChange, selectableTimezones, zoneMapping }}
    >
      {children}
    </DatePickerWithZoneContext.Provider>
  );
}
